import { useCallback, useEffect, useRef } from 'react';
import { Vertical } from '@weave/schema-gen-ts/dist/shared/vertical/vertical.pb';
import { useQueryClient } from 'react-query';
import {
  MockAPIRouter,
  MockInterceptorEntry,
  RequestInterceptorEntry,
  ResponseInterceptorEntry,
  useFetch,
} from '@frontend/fetch';
import { useAppScopeStore } from '@frontend/scope';
import { MockCallRecords } from '../call-recordings';
import {
  CallIntelInsightPanelMockRouter,
  CallIntelOverviewMockRouter,
  CallIntelRecordsMockRouter,
  CallIntelWidgetsMockContext,
  ContactPanelMockRouter,
} from '../feature-mock-routers';
import { MockCallFollowUps } from '../follow-up';
import { MockPersons } from '../persons';

interface UseCallIntelWidgetDemoInterceptorsProps {
  locationIds: string[];
  interceptorIdPrefix?: string;
  enabled?: boolean;
}

export const useCallIntelWidgetDemoInterceptors = ({
  locationIds,
  interceptorIdPrefix = 'call-intel-dashboard-widgets-demo',
  enabled,
}: UseCallIntelWidgetDemoInterceptorsProps) => {
  const { accessibleLocationData } = useAppScopeStore();
  const {
    addMockInterceptor,
    addRequestInterceptor,
    addResponseInterceptor,
    removeRequestInterceptor,
    removeResponseInterceptor,
    removeMockInterceptor,
  } = useFetch();

  const vertical = accessibleLocationData?.[locationIds[0]]?.vertical || Vertical.DENTAL;
  const queryClient = useQueryClient();
  const interceptorsInitializedRef = useRef(false);

  const getMockDataContext = useCallback((): CallIntelWidgetsMockContext => {
    const mockPersonsInstance = new MockPersons(50);
    const mockFollowUpsInstance = new MockCallFollowUps({
      count: 50,
      persons: mockPersonsInstance.persons,
      vertical: vertical,
      locationId: locationIds[0],
    });
    const mockCallRecordsInstance = new MockCallRecords({
      count: 50,
      vertical,
      persons: mockPersonsInstance.persons,
      locationId: locationIds[0],
    });
    return {
      vertical,
      locationId: locationIds[0],
      mockPersonsInstance,
      mockCallRecordsInstance,
      interceptorIdPrefix,
      mockFollowUpsInstance,
      queryClient,
    };
  }, [interceptorIdPrefix, locationIds, vertical, queryClient]);

  const getMockRouter = useCallback(
    (context: CallIntelWidgetsMockContext): MockInterceptorEntry => {
      const mockApiRouter = new MockAPIRouter();
      const mockInteceptorId = `${interceptorIdPrefix}-mock-interceptor`;

      // Mock endpoints for call intelligence overview
      CallIntelOverviewMockRouter.addMockRouterEndpoints?.(mockApiRouter, context);
      // Mock endpoints for patient panel
      ContactPanelMockRouter.addMockRouterEndpoints?.(mockApiRouter, context);
      // Mock endpoints for call recordings
      CallIntelRecordsMockRouter.addMockRouterEndpoints?.(mockApiRouter, context);
      // Mock endpoints for call insight panel
      CallIntelInsightPanelMockRouter.addMockRouterEndpoints?.(mockApiRouter, context);

      return {
        id: mockInteceptorId,
        mockApiRouter,
      };
    },
    [interceptorIdPrefix]
  );

  const getRequestInterceptors = useCallback((context: CallIntelWidgetsMockContext) => {
    return [
      ...(CallIntelOverviewMockRouter.getRequestInterceptors?.(context) || []),
      ...(CallIntelRecordsMockRouter.getRequestInterceptors?.(context) || []),
      ...(ContactPanelMockRouter.getRequestInterceptors?.(context) || []),
      ...(CallIntelInsightPanelMockRouter.getRequestInterceptors?.(context) || []),
    ];
  }, []);

  const getResponseInterceptors = useCallback((context: CallIntelWidgetsMockContext) => {
    return [
      ...(CallIntelOverviewMockRouter.getResponseInterceptors?.(context) || []),
      ...(CallIntelRecordsMockRouter.getResponseInterceptors?.(context) || []),
      ...(ContactPanelMockRouter.getResponseInterceptors?.(context) || []),
      ...(CallIntelInsightPanelMockRouter.getResponseInterceptors?.(context) || []),
    ];
  }, []);

  // Invalidate queries for all interceptors
  const invalidateQueries = useCallback(() => {
    CallIntelOverviewMockRouter.invalidateQueries?.(queryClient);
    CallIntelRecordsMockRouter.invalidateQueries?.(queryClient);
    ContactPanelMockRouter.invalidateQueries?.(queryClient);
    CallIntelInsightPanelMockRouter.invalidateQueries?.(queryClient);
  }, [queryClient]);

  useEffect(() => {
    let requestInterceptors: RequestInterceptorEntry[] = [];
    let responseInterceptors: ResponseInterceptorEntry[] = [];
    let mockInterceptor: MockInterceptorEntry | null = null;

    const cleanup = () => {
      if (interceptorsInitializedRef.current) {
        mockInterceptor?.id && removeMockInterceptor(mockInterceptor.id);
        requestInterceptors.map(({ id }) => id).forEach(removeRequestInterceptor);
        invalidateQueries();
      }
    };

    if (enabled) {
      const context = getMockDataContext();
      requestInterceptors = getRequestInterceptors(context);
      responseInterceptors = getResponseInterceptors(context);
      mockInterceptor = getMockRouter(context);

      interceptorsInitializedRef.current = true;

      addMockInterceptor(mockInterceptor);
      requestInterceptors.forEach(addRequestInterceptor);
      responseInterceptors.forEach(addResponseInterceptor);
      invalidateQueries();
    } else {
      cleanup();
    }

    return cleanup;
  }, [
    enabled,
    addMockInterceptor,
    addRequestInterceptor,
    addResponseInterceptor,
    removeMockInterceptor,
    queryClient,
    getMockRouter,
    getRequestInterceptors,
    getResponseInterceptors,
    removeRequestInterceptor,
    removeResponseInterceptor,
    getMockDataContext,
    invalidateQueries,
  ]);
};
